import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Share } from '@capacitor/share';
import { toast } from "sonner";

// Import circular icons from assets
import FacebookIcon from "@/assets/Facebook.png";
import XIcon from "@/assets/X.png";
import WhatsAppIcon from "@/assets/WhatsApp.png";
import WeChatIcon from "@/assets/WeChat.png";
import MessengerIcon from "@/assets/Messenger.png";
import TelegramIcon from "@/assets/Telegram.png";
import DiscordIcon from "@/assets/Discord.png";
import EmailIcon from "@/assets/email.png";
import CopyLinkIcon from "@/assets/Copy Link.png";
import OtherAppsIcon from "@/assets/ShareLink1.png";

interface GenericShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  shareText: string;
  shareUrl: string;
}

export default function GenericShareModal({ 
  isOpen, 
  onClose, 
  title,
  description,
  shareText, 
  shareUrl 
}: GenericShareModalProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [wechatCopied, setWechatCopied] = useState(false);
  const [discordCopied, setDiscordCopied] = useState(false);

  const getFullUrl = () => {
    // Ensure URL is absolute
    if (shareUrl.startsWith('http')) return shareUrl;
    return `${window.location.origin}${shareUrl.startsWith('/') ? '' : '/'}${shareUrl}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${getFullUrl()}`);
      setCopied(true);
      toast.success(t('modals.share.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getFullUrl())}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
    toast.success(t('modals.share.openingFacebook', 'Opening Facebook'));
    onClose();
  };

  const handleShareX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(getFullUrl())}`;
    window.open(url, '_blank', 'width=600,height=400');
    toast.success(t('modals.share.openingX', 'Opening X'));
    onClose();
  };

  const handleShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n${getFullUrl()}`)}`;
    window.open(url, '_blank');
    toast.success(t('modals.share.openingWhatsApp', 'Opening WhatsApp'));
    onClose();
  };

  const handleShareWeChat = () => {
    navigator.clipboard.writeText(`${shareText}\n${getFullUrl()}`);
    setWechatCopied(true);
    setTimeout(() => setWechatCopied(false), 3000);
  };

  const handleShareMessenger = () => {
    const url = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(getFullUrl())}&app_id=YOUR_APP_ID&redirect_uri=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'width=600,height=400');
    toast.success(t('modals.share.openingMessenger', 'Opening Messenger'));
    onClose();
  };

  const handleShareTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(getFullUrl())}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
    toast.success(t('modals.share.openingTelegram', 'Opening Telegram'));
    onClose();
  };

  const handleShareDiscord = () => {
    navigator.clipboard.writeText(`${shareText}\n${getFullUrl()}`);
    setDiscordCopied(true);
    setTimeout(() => setDiscordCopied(false), 3000);
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(`${shareText}\n\n${getFullUrl()}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    toast.success(t('modals.share.openingEmail', 'Opening email client'));
    onClose();
  };

  const handleOtherApps = async () => {
    try {
      // Check if we're in a Capacitor mobile app environment
      const isCapacitorApp = !!(window as any).Capacitor;
      
      if (isCapacitorApp) {
        // Use Capacitor Share plugin for mobile apps (Android/iOS)
        const canShare = await Share.canShare();
        if (canShare.value) {
          await Share.share({
            title: title,
            text: shareText,
            url: getFullUrl(),
          });
          toast.success(t('modals.share.sharedSuccessfully', 'Shared successfully!'));
          onClose();
          return;
        }
      }
      
      // Fallback to Web Share API for web browsers
      if (navigator.share) {
        await navigator.share({
          title: title,
          text: shareText,
          url: getFullUrl(),
        });
        toast.success(t('modals.share.sharedSuccessfully', 'Shared successfully!'));
        onClose();
      } else {
        // Final fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareText}\n${getFullUrl()}`);
        toast.success(t('modals.share.linkCopiedFallback', 'Link copied to clipboard!'));
        onClose();
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('User cancelled sharing');
      } else {
        console.error('Sharing failed:', error);
        try {
          await navigator.clipboard.writeText(`${shareText}\n${getFullUrl()}`);
          toast.success(t('modals.share.linkCopiedFallback', 'Link copied to clipboard!'));
        } catch (clipboardError) {
          toast.error(t('modals.share.sharingFailed', 'Sharing failed'));
        }
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('modals.share.title')}</DialogTitle>
          <DialogDescription>
            {description || t('modals.share.descriptionGeneral')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          {/* 4x3 Grid Layout with Circular Icons */}
          
          {/* Row 1: Facebook, X, WhatsApp, WeChat */}
          <div className="grid grid-cols-4 gap-4 justify-items-center">
            <div className="flex flex-col items-center gap-1">
              <button
                className="w-16 h-16 rounded-full bg-[#1877F2] hover:bg-[#1877F2]/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#1877F2]/25 active:scale-95"
                onClick={handleShareFacebook}
                title="Facebook"
              >
                <img src={FacebookIcon} alt="Facebook" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.facebook')}</span>
            </div>
            
            <div className="flex flex-col items-center gap-1">
              <button
                className="w-16 h-16 rounded-full bg-black hover:bg-black/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-black/25 active:scale-95"
                onClick={handleShareX}
                title="X"
              >
                <img src={XIcon} alt="X" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">X</span>
            </div>
            
            <div className="flex flex-col items-center gap-1">
              <button
                className="w-16 h-16 rounded-full bg-[#25D366] hover:bg-[#25D366]/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#25D366]/25 active:scale-95"
                onClick={handleShareWhatsApp}
                title="WhatsApp"
              >
                <img src={WhatsAppIcon} alt="WhatsApp" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.whatsApp')}</span>
            </div>

            <div className="flex flex-col items-center gap-1">
              <button
                className={`w-16 h-16 rounded-full bg-[#1AAD19] hover:bg-[#1AAD19]/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#1AAD19]/25 active:scale-95 ${
                  wechatCopied ? 'ring-1 ring-green-500 animate-pulse' : ''
                }`}
                onClick={handleShareWeChat}
                title="WeChat"
              >
                <img src={WeChatIcon} alt="WeChat" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.weChat')}</span>
            </div>
          </div>

          {/* Row 2: Messenger, Telegram, Discord, Email */}
          <div className="grid grid-cols-4 gap-4 justify-items-center">
            <div className="flex flex-col items-center gap-1">
              <button
                className="w-16 h-16 rounded-full bg-[#0084FF] hover:bg-[#0084FF]/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#0084FF]/25 active:scale-95"
                onClick={handleShareMessenger}
                title="Messenger"
              >
                <img src={MessengerIcon} alt="Messenger" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.messenger')}</span>
            </div>

            <div className="flex flex-col items-center gap-1">
              <button
                className="w-16 h-16 rounded-full bg-[#0088CC] hover:bg-[#0088CC]/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#0088CC]/25 active:scale-95"
                onClick={handleShareTelegram}
                title="Telegram"
              >
                <img src={TelegramIcon} alt="Telegram" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.telegram')}</span>
            </div>

            <div className="flex flex-col items-center gap-1">
              <button
                className={`w-16 h-16 rounded-full bg-[#5865F2] hover:bg-[#5865F2]/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#5865F2]/25 active:scale-95 ${
                  discordCopied ? 'ring-1 ring-green-500 animate-pulse' : ''
                }`}
                onClick={handleShareDiscord}
                title="Discord"
              >
                <img src={DiscordIcon} alt="Discord" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.discord')}</span>
            </div>

            <div className="flex flex-col items-center gap-1">
              <button
                className="w-16 h-16 rounded-full bg-[#EA4335] hover:bg-[#EA4335]/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-[#EA4335]/25 active:scale-95"
                onClick={handleShareEmail}
                title="Email"
              >
                <img src={EmailIcon} alt="Email" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.email')}</span>
            </div>
          </div>

          {/* Row 3: Copy Link, Other Apps */}
          <div className="grid grid-cols-4 gap-4 justify-items-center">
            <div className="flex flex-col items-center gap-1">
              <button
                className={`w-16 h-16 rounded-full bg-purple-600 hover:bg-purple-600/80 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-purple-600/25 active:scale-95 ${
                  copied ? 'ring-1 ring-green-500 animate-pulse bg-green-600' : ''
                }`}
                onClick={handleCopyLink}
                title={t('modals.share.copyLink')}
              >
                <img 
                  src={CopyLinkIcon} 
                  alt="Copy Link" 
                  className="w-10 h-10 transition-transform duration-200"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.parentElement!.innerHTML = '<svg class="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd"></path></svg>';
                  }}
                />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.copyLink')}</span>
            </div>
            
            <div className="flex flex-col items-center gap-1">
              <button
                className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-purple-500/25 active:scale-95"
                onClick={handleOtherApps}
                title={t('modals.share.otherApps', 'Other Apps')}
              >
                <img src={OtherAppsIcon} alt="Other Apps" className="w-10 h-10 transition-transform duration-200" />
              </button>
              <span className="text-xs text-muted-foreground">{t('modals.share.otherApps')}</span>
            </div>
          </div>

          {/* Enhanced Status Messages with animations */}
          {(copied || wechatCopied || discordCopied) && (
            <div className="text-center animate-fade-in">
              <div className="inline-flex items-center bg-green-50 text-green-700 px-4 py-2 rounded-full">
                <svg className="w-4 h-4 mr-2 animate-bounce" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">
                  {copied && t('modals.share.copied')}
                  {wechatCopied && t('modals.share.wechatInstructions', 'Link copied! Paste in WeChat')}
                  {discordCopied && t('modals.share.discordInstructions', 'Link copied! Paste in Discord')}
                </span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
